import { useEffect, useState, useRef, type CSSProperties, Fragment} from 'react';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router';
import { type Optional } from '@/types/types';
import { type ScrollEventData, type ScrollEventHandler, ScrollMessage} from '@/types/scroll-events';
import Authorizor from '@/util/authorizor';
import { type Person, PersonRole } from '@/models/person';
import { keyCompetitionJumpWithIdTeamMembers } from '@/api/query-keys';
import { getTeamMembersForJumpWithId } from '@/api/service/competition.service';
import { useChatState, type ChatState } from '@/hooks/useChatState.hook';
import { useScrollEventSubscription } from '@/hooks/useScrollEvent.hook';
import ChatBar, { CHATBAR_HEIGHT, CHATBAR_REPLYING_HEIGHT } from '@/components/ChatBar';
import Authorized from '@/components/Authorized';
import Jump from '@/components/Jump';
import Loading from '@/components/Loading';
import { classes } from '@/util/component-utils';
import styles from './draftsubmission.module.css';

const route = '/draftsubmission/$submissionId';
export const Route = createFileRoute(route)({
  component: DraftJumpPage,
})

const logRoute = () => {
  console.log("Draft jump route");
}

const chatHiddenStyle = {
  height: "100%"
};
const chatVisibleStyle = {
  height: `calc(100% - ${CHATBAR_HEIGHT})`
}
const chatVisibleWithReplyStyle = {
  height: `calc(100% - ${CHATBAR_REPLYING_HEIGHT})`
}

function DraftJumpPage() {
  const { submissionId } = useParams({ from: route });
  const [isGuest, setIsGuest] = useState(false);
  const { data, isLoading } = useQuery({
      queryKey: keyCompetitionJumpWithIdTeamMembers(submissionId), 
      queryFn: async () => getTeamMembersForJumpWithId(submissionId)
  });
  const [containerStyles, setContainerStyles] = useState<CSSProperties>(chatHiddenStyle);
  const chatVisible = useChatState((state: ChatState) => state.visible);
  const initChat = useChatState(((state: ChatState) => state.init));
  const chatTarget = useChatState((state: ChatState) => state.target);
  const startRef = useRef<HTMLDivElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const userIsTeamMember = (team: Person[]) => {
    for (const person of team) {
      if (person.email.toLowerCase() === Authorizor.profile?.email) {
        return true;
      }
    }
    return false;
  }

  const userIsInvited = (jumpId: string) => {
    const invitations = Authorizor.profile?.invitations;
    if (!invitations) return false;
    for (const invitation of invitations) {
      if (invitation.jumpId == jumpId) {
        return true;
      }
    }
  }

  const handleScrollEvent: ScrollEventHandler = (data: ScrollEventData) => {
    if (chatVisible) {
      setTimeout(() => {
        let element: Optional<HTMLElement> | null = null;
        switch(data.message) {
          case ScrollMessage.START:
            element = startRef.current;
            break;
          case ScrollMessage.END:
            element = endRef.current;
            break;
          case ScrollMessage.ELEMENT:
            element = data.element;
        }
        element?.scrollIntoView({ behavior: "smooth" });
      }, 250);
    }
  }

  logRoute();

  useScrollEventSubscription(submissionId, handleScrollEvent);

  useEffect(() => {
    initChat();
  }, [])

  useEffect(() => {
    if (data && !isLoading) {
      if (userIsTeamMember(data))
        setIsGuest(false);
    } else if (!userIsInvited(submissionId)) {
      navigate({ to: "/home" });
    }
  }, [data, isLoading]);

  useEffect(() => {
    let visibleStyle = chatVisibleStyle;
    if (chatVisible && chatTarget) {
      visibleStyle = chatVisibleWithReplyStyle;
    }
    setContainerStyles(chatVisible ? visibleStyle : chatHiddenStyle);
  }, [chatVisible, chatTarget]);

  if (isLoading) {
    return <Loading />
  }

  return (
    <Fragment>
      <Authorized>
        <div className={classes("scrolling-container", styles.jumpContainer)} style={containerStyles}>
          <div ref={startRef} />
          <Jump userRole={isGuest ? PersonRole.REVIEWER : PersonRole.JUMP_ADMIN } jumpId={submissionId} />
          <div ref={endRef} />
        </div>
        <ChatBar guest={false} jumpId={submissionId} />
      </Authorized>
    </Fragment>
  )
}
